import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { LOGIN_OK } from '../common/constants';
import { IGestures } from '../common/interfaces';
import { VUOnboardingAudioESComponent } from '../common/components';
import { CustomButtonComponent } from '@shared/components/button/button.component';
import {
  biometricValidationMethod,
  buttonConfig,
  customComponentListener,
  labelType,
} from '@core/types/types';
import {
  biometricCaptureFaceSvgIcon,
  spinnerSvgIcon,
} from '@assets/icons/icons';
import { IconsComponent } from '@shared/components/icons/icon.component';
import {
  bootstrapArrowClockwise,
  bootstrapCamera,
  bootstrapCheck,
  bootstrapInfo,
  bootstrapInfoCircle,
  bootstrapXCircleFill,
  bootstrapXLg,
} from '@ng-icons/bootstrap-icons';
import { Subscription } from 'rxjs';
import {
  AssistedPatientService,
  BeneficiaryService,
  BiometricService,
  ComponentUtilsService,
} from '@core/services';
import { TranslateModule } from '@ngx-translate/core';
import { ClaimService } from '@core/services/http/claim.service';

const faceCaptureStatuses = {
  'in-progress': 'in-progress',
  captured: 'captured',
  failed: 'failed',
  retrying: 'retrying',
  validating: 'validating',
  validated: 'validated',
  invalid: 'invalid',
};

type faceCaptureStatus = keyof typeof faceCaptureStatuses;

declare let vu: any;

export type onBiometricFaceCapturedEvent = {
  method: biometricValidationMethod;
  faceCapture: string;
  biometricValidationId?: string;
};

@Component({
  selector: 'vu-face',
  templateUrl: './face.component.html',
  styleUrls: ['./face.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    VUOnboardingAudioESComponent,
    CustomButtonComponent,
    IconsComponent,
    TranslateModule,
  ],
})
export class VUOnboardingFaceComponent implements OnInit, OnDestroy {
  @Input({ required: false }) listeners!: customComponentListener[];

  @Output()
  onBiometricFaceCaptured: EventEmitter<onBiometricFaceCapturedEvent> =
    new EventEmitter();
  @Output()
  onBiometricFaceValidated: EventEmitter<onBiometricFaceCapturedEvent> =
    new EventEmitter();

  @Input() loadingImg?: string;
  @Input() enableAudio: boolean = false;
  @Input() gestures: Partial<IGestures> | null = {
    resultsFeedbackPercentual: 50,
    resultsFeedbackTimeFrame: 1500,
    resultsValidateTimeFrame: 3500,
    allChallenges: [],
    numOfChallenges: 1,
    permisiveNeutralChallenge: false,
  };
  @Input() config: { [key: string]: any } = {};

  isFirefox = signal<boolean>(/firefox|fxios/i.test(navigator.userAgent));
  hasCameraPermission = signal<boolean>(false);

  show = signal<boolean>(false);

  private biometricService = inject(BiometricService);
  private utils = inject(ComponentUtilsService);
  private assistedPatientService = inject(AssistedPatientService);

  protected selectedCompanion = this.assistedPatientService.selectedCompanion;
  private biometric$?: Subscription;

  protected signals = {
    faceCapture: {
      status: signal<faceCaptureStatus | undefined>(undefined),
      statusMotive: signal<labelType | undefined>(undefined),
      values: signal<string[]>([]),
    },
  };

  protected icons = {
    camera: bootstrapCamera,
    check: bootstrapCheck,
    spinner: spinnerSvgIcon,
    x: bootstrapXCircleFill,
    info: bootstrapInfoCircle
  };

  protected btns = computed(() => ({
    capture: {
      type: 'button',
      label: {
        key: `app.pageId.biometric.common.${
          !(this.signals.faceCapture.status() == 'in-progress')
            ? 'capture'
            : 'capturing'
        }`,
      },
      startContent: {
        content: IconsComponent,
        data: {
          icon: biometricCaptureFaceSvgIcon,
          size: 20,
          color: 'white',
        },
      },
      hidden:
        this.signals.faceCapture.status() &&
        !this.signals.faceCapture
          .status()
          ?.includes(faceCaptureStatuses['in-progress']),
      disabled: this.signals.faceCapture.status() == 'in-progress',
      action: this.initCaptureFace.bind(this),
    } as buttonConfig,
    retryCapture: {
      type: 'button',
      label: {
        key: `app.pageId.biometric.common.${
          !(this.signals.faceCapture.status() == 'retrying')
            ? 'retry'
            : 'retrying'
        }`,
      },
      color: 'secondary',
      startContent: {
        content: IconsComponent,
        data: {
          icon: bootstrapArrowClockwise,
          size: 25,
          color: 'white',
        },
      },
      hidden:
        this.signals.faceCapture.status() == undefined ||
        this.signals.faceCapture
          .status()
          ?.includes(faceCaptureStatuses['in-progress']) ||
        this.signals.faceCapture
          .status()
          ?.includes(faceCaptureStatuses.validating) ||
        this.signals.faceCapture
          .status()
          ?.includes(faceCaptureStatuses.captured),
      action: this.retryCaptureFace.bind(this),
      disabled: this.signals.faceCapture.status() == 'retrying',
    } as buttonConfig,
    validateCapture: {
      type: 'button',
      label: {
        key: `app.pageId.biometric.common.${
          this.signals.faceCapture.status() == 'captured' ||
          this.signals.faceCapture.status() == 'validated'
            ? 'validate'
            : 'validating'
        }`,
      },
      color: 'primary',
      startContent: {
        content: IconsComponent,
        data: {
          icon: bootstrapCheck,
          size: 25,
          color: 'white',
        },
      },
      hidden:
        this.signals.faceCapture.values().length <= 0 ||
        this.signals.faceCapture.status() == 'failed' ||
        this.signals.faceCapture.status() == 'invalid',
      action: this.validateFace.bind(this),
      disabled: this.signals.faceCapture.status() == 'validating',
    } as buttonConfig,
    biometricIndications: {
        type: 'icon',
        icon: this.icons.info,
        label: {
          key: 'app.pageId.biometric.messages.biometric-extra-info'
        },
        color: 'primary',
        size: 'md',
        theme: 'ghost',
    } as buttonConfig,
  }));

  ngOnInit(): void {
    this.utils.attachListeners(this, this.listeners);

    vu.face.useGestures = !!this.gestures;

    vu.sop.audio.enabled = this.enableAudio;
    vu.face.auth.userNameValue = '*****';

    if (this.loadingImg) {
      vu.sop.loadingImgSrc = this.loadingImg;
      vu.face.auth.loadingImgSrc = this.loadingImg;
    }

    vu.face.auth.load('assets/vu-sdk').then(
      () => {
        vu.sop.api.faceLogin = () => Promise.resolve({ code: LOGIN_OK });

        Object.assign(vu.face.ui.gestures, this.gestures);
        Object.assign(vu, this.config);

        vu.face.gestures.circleActiveColor = '#3e9671';
        vu.face.gestures.circleDetectedColor = '#e8ab13';
        vu.face.gestures.circleInactiveColor = '#f6f6f6';
        vu.face.gestures.lineWidth = 7;
      },
      (err: any) => console.error('Error precargando SDK:', err)
    );
  }

  initCaptureFace = () => {
    this.requestCameraPermission().then((granted) => {
      if (granted) {
        setTimeout(() => {
          this.show.set(!this.show());
          this.signals.faceCapture.status.set('in-progress');
          this.start();
        }, 0);
      }
    });
  };

  private async requestCameraPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  }

  private async start() {
    try {
      await vu.face.auth.start();

      const image = vu.face.ui.gestures.pictures.at(-1);
      const base64Img = vu.sop.api.imgData2b64(image);

      this.stop();
      this.signals.faceCapture.status.set('captured');

      this.signals.faceCapture.values.set([base64Img]);

      this.show.set(!this.show());

      this.onBiometricFaceCaptured.emit({
        method: 'face',
        faceCapture: base64Img,
        biometricValidationId: undefined,
      });

      this.validateFace();
    } catch (error) {
      this.signals.faceCapture.status.set('failed');
      this.signals.faceCapture.statusMotive.set({ key: error });
    }
  }

  private stop() {
    vu.face.auth.videoResizeObserver.disconnect();
    vu.camera.track.stop();
  }

  ngOnDestroy(): void {
    this.stop();
    this.biometric$?.unsubscribe();
  }

  private claimService = inject(ClaimService);
  private beneficiaryService = inject(BeneficiaryService);

  validateFace = () => {
    const initialClaimInfo = this.claimService.initialClaimInfo()!;
    const selectedBeneficiary = this.beneficiaryService.selectedBeneficiary()!;

    this.signals.faceCapture.status.set('validating');

    const validateBiometricPayload = this.selectedCompanion()
      ? {
          isPatientAssisted: true,
          identificationNumber: this.selectedCompanion()!.identificationNumber,
          biometricType: 'LECTURA_FACIAL',
          img: this.signals.faceCapture.values()[0],
        }
      : {
          isPatientAssisted: false,
          beneficiaryId: selectedBeneficiary.id,
          providerSlug: initialClaimInfo.providerSlug,
          biometricType: 'LECTURA_FACIAL',
          img: this.signals.faceCapture.values()[0],
        };

    this.biometric$ = this.biometricService
      .validateBiometric(
        validateBiometricPayload as any,
        initialClaimInfo.token!
      )
      .subscribe(
        (response) => {
          if (response.data.status == 'VERIFICATION_SUCCESS') {
            this.signals.faceCapture.status.set('validated');
            this.onBiometricFaceValidated.emit({
              method: 'face',
              faceCapture: this.signals.faceCapture.values()[0],
              biometricValidationId: response.data.biometricValidationId,
            });
          } else {
            this.signals.faceCapture.statusMotive.set({
              key: response.data.statusMessage,
            });
            this.signals.faceCapture.status.set('invalid');
          }
        },
        (error: any) => {
          this.signals.faceCapture.statusMotive.set({
            key: error.data.statusMessage,
          });
          this.signals.faceCapture.status.set('invalid');
        }
      );
  };

  retryCaptureFace = () => {
    this.signals.faceCapture.values.set([]);
    this.signals.faceCapture.status.set('retrying');
    this.signals.faceCapture.statusMotive.set(undefined);

    this.onBiometricFaceCaptured.emit(undefined);

    document.getElementById('vu.sop')!.innerHTML = '';

    this.show.set(!this.show());

    vu.face.useGestures = !!this.gestures;

    vu.sop.audio.enabled = this.enableAudio;
    vu.face.auth.userNameValue = '*****';

    if (this.loadingImg) {
      vu.sop.loadingImgSrc = this.loadingImg;
      vu.face.auth.loadingImgSrc = this.loadingImg;
    }

    vu.face.auth.load('assets/vu-sdk').then(
      () => {
        vu.sop.api.faceLogin = () => Promise.resolve({ code: LOGIN_OK });

        Object.assign(vu.face.ui.gestures, this.gestures);
        Object.assign(vu, this.config);

        vu.face.gestures.circleActiveColor = '#3e9671';
        vu.face.gestures.circleDetectedColor = '#e8ab13';
        vu.face.gestures.circleInactiveColor = '#f6f6f6';
        vu.face.gestures.lineWidth = 7;

        this.start();
      },
      (err: any) => console.error('Error precargando SDK:', err)
    );
  };
}
